/**
 * Finished session ZIPs waiting for the validation database. Each one stays in this browser (IndexedDB)
 * until the server returns a receipt that matches its bytes, so a network failure or a page reload never
 * loses a session. Uploads are idempotent on the server, so Retry resends the identical ZIP.
 */
import type { TrainingUploadReceipt, ValidationType } from "@myndland/game-core";

export type PendingUpload = {
  id: string;
  filename: string;
  zip: Uint8Array;
  createdAt: number;
  subjectId: number;
  sessionId: string;
  program: string;
  moduleName: string;
  moduleType: ValidationType;
  /** Simulated EEG is kept for download but never uploaded. */
  simulated: boolean;
  message: string;
  retryable: boolean;
  receipt?: TrainingUploadReceipt;
};

export interface PendingStore {
  list(): Promise<PendingUpload[]>;
  put(item: PendingUpload): Promise<void>;
  delete(id: string): Promise<void>;
}

export function memoryStore(): PendingStore {
  const items = new Map<string, PendingUpload>();
  return {
    list: async () => [...items.values()].sort((a, b) => a.createdAt - b.createdAt),
    put: async (item) => { items.set(item.id, item); },
    delete: async (id) => { items.delete(id); },
  };
}

const req = <T>(r: IDBRequest<T>) => new Promise<T>((resolve, reject) => { r.onsuccess = () => resolve(r.result); r.onerror = () => reject(r.error); });

/** IndexedDB store; falls back to memory (with a console warning) where IndexedDB is unavailable. */
export async function openPendingStore(name = "myndland-validation-sessions"): Promise<{ store: PendingStore; durable: boolean }> {
  try {
    const open = indexedDB.open(name, 1);
    open.onupgradeneeded = () => open.result.createObjectStore("uploads", { keyPath: "id" });
    const db = await req(open);
    const tx = (mode: IDBTransactionMode) => db.transaction("uploads", mode).objectStore("uploads");
    return {
      durable: true,
      store: {
        list: async () => ((await req(tx("readonly").getAll())) as PendingUpload[]).sort((a, b) => a.createdAt - b.createdAt),
        put: async (item) => { await req(tx("readwrite").put(item)); },
        delete: async (id) => { await req(tx("readwrite").delete(id)); },
      },
    };
  } catch (e) {
    console.warn("[session] IndexedDB unavailable; finished sessions are kept in memory only until uploaded or downloaded:", e);
    return { store: memoryStore(), durable: false };
  }
}
